import React from 'react';
import { Shield, CheckCircle, Award, Users, FileText, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const Compliance = () => {
  const complianceAreas = [
    {
      icon: <Shield className="h-8 w-8 text-green-500" />,
      title: "Ministry of Health (MOH)",
      description: "Full compliance with Singapore's MOH standards and regulations for food safety"
    },
    {
      icon: <Award className="h-8 w-8 text-blue-500" />,
      title: "IDDSI Standards",
      description: "International Dysphagia Diet Standardisation Initiative compliance for therapeutic diets"
    },
    {
      icon: <FileText className="h-8 w-8 text-purple-500" />,
      title: "Food Safety Protocols",
      description: "Comprehensive food safety management systems and regular audits"
    },
    {
      icon: <Users className="h-8 w-8 text-orange-500" />,
      title: "Staff Certification",
      description: "Ongoing training and certification programs for all food handling personnel"
    }
  ];

  const certifications = [
    {
      name: "Basic Food Hygiene",
      category: "Foundation Certification",
      description: "Essential certification ensuring proper food handling and hygiene practices across all operations"
    },
    {
      name: "Advanced Food Hygiene Officer",
      category: "Management Certification", 
      description: "Advanced certification for food safety management, supervision, and regulatory compliance"
    },
    {
      name: "Eatsafe SG (IDDSI)",
      category: "Specialized Certification",
      description: "International Dysphagia Diet Standardisation Initiative certification for therapeutic diet preparation"
    }
  ];

  const safetyMeasures = [
    "Temperature monitoring and control systems",
    "Hazard Analysis and Critical Control Points (HACCP)",
    "Regular microbiological testing and analysis",
    "Supplier verification and approval processes",
    "Equipment sanitization and maintenance protocols",
    "Allergen management and cross-contamination prevention",
    "Waste management and environmental health protocols",
    "Personal hygiene and protective equipment requirements"
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-red-600 text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Compliance & Safety</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Maintaining the highest standards of food safety and regulatory compliance 
              through rigorous protocols, continuous training, and unwavering commitment to excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Uncompromising Food Safety Standards
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  At Teng Food Holdings, food safety is not just a requirement—it's our fundamental 
                  commitment to protecting public health and maintaining the trust our customers 
                  place in us. We maintain strict compliance with all local and international 
                  food safety standards and regulations.
                </p>
                <p>
                  Our comprehensive approach to compliance encompasses every aspect of our operations, 
                  from raw material sourcing to final product delivery, ensuring consistent quality 
                  and safety across all our manufacturing facilities.
                </p>
              </div>
            </div>

            <div>
              <img 
                src="https://images.unsplash.com/photo-1563468304224-1fc761a1cbb5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxjb21tZXJjaWFsJTIwa2l0Y2hlbnxlbnwwfHx8fDE3NjAwMDM3ODB8MA&ixlib=rb-4.1.0&q=85"
                alt="Food Safety Compliance"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Areas */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Key Compliance Areas
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our compliance framework covers all critical areas of food safety 
              and regulatory requirements to ensure comprehensive protection.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {complianceAreas.map((area, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-4">
                    {area.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{area.title}</h3>
                  <p className="text-gray-600">{area.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Staff Certifications */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Staff Certifications
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our team holds various professional certifications ensuring expert knowledge 
              in food safety management and specialized diet preparation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {certifications.map((cert, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Award className="h-8 w-8 text-yellow-500" />
                    <div>
                      <h3 className="font-semibold text-gray-900">{cert.name}</h3>
                      <span className="text-sm text-blue-600">{cert.category}</span>
                    </div>
                  </div>
                  <p className="text-gray-600">{cert.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Measures */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Comprehensive Safety Measures
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We implement a multi-layered approach to food safety that addresses 
                every potential risk point in our manufacturing processes.
              </p>

              <div className="space-y-3">
                {safetyMeasures.map((measure, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">{measure}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <img 
                src="https://images.unsplash.com/photo-1589109807644-924edf14ee09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwa2l0Y2hlbnxlbnwwfHx8fDE3NjAwMDM3ODB8MA&ixlib=rb-4.1.0&q=85"
                alt="Safety Protocols"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Continuous Improvement */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Continuous Improvement
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              Our commitment to excellence drives us to continuously enhance our 
              compliance and safety protocols through regular reviews and updates.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center">
              <CardContent className="p-6">
                <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-3">Risk Assessment</h3>
                <p className="text-gray-600">
                  Regular risk assessments to identify and mitigate potential food safety hazards
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="p-6">
                <Users className="h-12 w-12 text-blue-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-3">Staff Training</h3>
                <p className="text-gray-600">
                  Ongoing training programs to keep staff updated on latest safety protocols
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="p-6">
                <FileText className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-3">Regular Audits</h3>
                <p className="text-gray-600">
                  Internal and external audits to ensure consistent compliance with all standards
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-red-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Questions About Our Compliance Standards?
          </h2>
          <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
            Contact our compliance team to learn more about our food safety protocols 
            and how we ensure the highest standards in all our operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors">
              Contact Compliance Team
            </button>
            <button className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-3 rounded-lg font-semibold transition-colors">
              View Certifications
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Compliance;